import { STATUSES } from "@/components/FeedbackBadge";

// The kanban is a projection of the tracker, not a second store: every column
// is a status the API already knows, every move is the PATCH the detail panel
// already makes. This module is the pure part -- what goes where -- so it can
// be tested without a DOM.

export const DONE_STATUSES = new Set(["Resolved", "Closed"]);
export const GROUP_OPTIONS = [
  { value: "none", label: "Fără grupare" },
  { value: "project", label: "Proiect" },
  { value: "app", label: "Aplicație" },
  { value: "assignee", label: "Responsabil" },
  { value: "priority", label: "Prioritate" },
];
const PRIORITY_RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const PRIORITY_ORDER = ["Critical", "High", "Medium", "Low"];

const ts = (iso) => { const t = Date.parse(iso || ""); return Number.isNaN(t) ? 0 : t; };

// Inside a column: hottest first, then most recently touched.
export function sortForColumn(list) {
  return [...list].sort((a, b) =>
    (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0)
    || ts(b.updated_at || b.created_at) - ts(a.updated_at || a.created_at));
}

export function laneOf(t, groupBy, { nameOf, projectName }) {
  switch (groupBy) {
    case "project": {
      const name = t.project_id ? projectName(t.project_id) : null;
      return { key: `p:${t.project_id || `app:${t.app}`}`, label: name || t.app || "Fără proiect" };
    }
    case "app": return { key: `a:${t.app || ""}`, label: t.app || "Fără aplicație" };
    case "assignee": return t.assignee_id
      ? { key: `u:${t.assignee_id}`, label: nameOf(t.assignee_id) || "Necunoscut" }
      : { key: "u:", label: "Neatribuite" };
    case "priority": return { key: `pr:${t.priority}`, label: t.priority || "?" };
    default: return { key: "all", label: "" };
  }
}

function laneSort(groupBy) {
  if (groupBy === "priority") {
    return (a, b) => PRIORITY_ORDER.indexOf(a.label) - PRIORITY_ORDER.indexOf(b.label);
  }
  // Named lanes alphabetically; the catch-all ("Neatribuite") last.
  return (a, b) => {
    const ca = a.key.endsWith(":") ? 1 : 0, cb = b.key.endsWith(":") ? 1 : 0;
    return ca - cb || a.label.localeCompare(b.label);
  };
}

/**
 * tickets -> { columns: [status...], lanes: [{ key, label, count, cells: { [status]: ticket[] } }] }
 * showDone=false hides the two terminal columns and the tickets in them.
 */
export function buildBoard(tickets, { groupBy = "none", showDone = true, nameOf, projectName }) {
  const columns = showDone ? [...STATUSES] : STATUSES.filter((s) => !DONE_STATUSES.has(s));
  const visible = new Set(columns);
  const lanes = new Map();
  for (const t of tickets) {
    if (!visible.has(t.status)) continue;
    const lane = laneOf(t, groupBy, { nameOf, projectName });
    if (!lanes.has(lane.key)) {
      lanes.set(lane.key, { ...lane, count: 0, cells: Object.fromEntries(columns.map((c) => [c, []])) });
    }
    const L = lanes.get(lane.key);
    L.cells[t.status].push(t);
    L.count += 1;
  }
  const out = [...lanes.values()].sort(laneSort(groupBy));
  for (const L of out) for (const c of columns) L.cells[c] = sortForColumn(L.cells[c]);
  return { columns, lanes: out };
}

export const isMyWork = (t, userId) =>
  !!userId && (t.assignee_id === userId || t.reporter_id === userId);
